import {
  IThreeLayerID,
  IR2Paths,
  IEntityBase,
  IMetadata
} from '../types/base';
import { IOwnership } from '../types/ownership';
import { IVisibilityChild } from '../types/visibility';
import { IFork } from '../types/fork';
import { IVersionContainer } from '../types/version';
import { ITreeStructure } from '../types/tree';
import { IAction } from './IAction';
import { createMergeToDefaultData, mergeDataWith } from '../utils/mergeToDefaultData';
import { ulid } from 'ulid';
import { Log } from '@fanfanlo';

const fileLog = new Log(false, 'IActionGroup_f')
fileLog.pause = true;
fileLog.childrenPaused = true;

// === Action 数据变更接口 ===
/**
 * Action 数据变更属性接口
 * 用于 onClickChange 回调
 */
export interface IOnChangeActionJavaDataProps {
    query: any  // IActionOrTriggerListPageParams 类型，但为了避免循环依赖使用 any
    // 采用projectEventUtils.dispatchEditEvent的目的是能够在页面刷新后依然能通过url数据更新javaData
    // 但是如果使用注入函数的方法就做不到这点了，所以这个参数当前分析是不要用
    // 以后如果要启用，需要先考虑一下上面用projectEventUtils.dispatchEditEvent的优点是否能被替代才行。
    // 但是现在也面临修改数据后，其它未被修改的数据会需要重新初始化的问题
    // 而projectEventUtils.dispatchEditEvent里是merge，并没有重新初始化其它数据的功能。
    // 再次论证。这个方法现在是刷新页面即丢失的，并不是能够通过url来更新数据的。所以似乎可以启用这个方法
    // onJavaDataChangeDataSelect?:(data:any)=>void
    // 尝试考虑用这个方式来替代上面注入函数的修改方式。
    // 查找到action后，用action proxy的dispatcher派发事件来更新数据
    onDataSelectedEventType?: string
}

// === 类型常量 ===
export const actionGroupType = 'actionGroup'

/**
 * 动作组接口
 * 对应SQL表：project_action_group
 * 对应Go结构：ProjectActionGroup
 *
 * list字段存储的是子action的ULID列表
 * children字段存储完整的子action对象列表（运行时填充）
 */
export interface IActionGroup extends
  IThreeLayerID,
  IEntityBase,
  IR2Paths,
  IOwnership,
  IVisibilityChild,
  IFork,
  IVersionContainer,
  ITreeStructure<IAction>,
  IMetadata
{
  // list字段继承自ITreeStructure，存储子action的ULID
  // children字段继承自ITreeStructure<IAction>，存储完整对象
}

// === ActionGroup Utils ===
function createDefaultActionGroup(): IActionGroup {
  return {
    ulid: ulid(),
    uuid: '',
    list: [],
    children: [],
    checks: [],
    isSequential: true,
    createdAt: Date.now(),
    updatedAt: Date.now()
  } as IActionGroup
}

export interface IActionGroupUtils {
  type: string
  createDefaultData: () => IActionGroup
  mergeDataToDefaultData: (data: Partial<IActionGroup>) => IActionGroup
}

export const actionGroupUtils: IActionGroupUtils = {
  type: actionGroupType,
  createDefaultData: createDefaultActionGroup,
  mergeDataToDefaultData: createMergeToDefaultData(createDefaultActionGroup)
}

// === Action Registry ===
export interface IActionUtils<T extends IAction> {
  type: string
  init: () => void
  mergeDataToDefaultData: (data: Partial<T>) => T
  createDefaultData: () => T
  create: (actionGroup: IActionGroup, data?: Partial<T>) => void
  update: (action: T, data?: Partial<T>) => void
  UI: (data: T) => React.ReactNode
  onClickChange?: (data: IOnChangeActionJavaDataProps, ...rest: any[]) => void
  loadI18nTexts?: (data: T) => Promise<void>
}

export interface IActionUtilsMap {
  [key: string]: IActionUtils<any>
}

export const ActionUtilsMap: IActionUtilsMap = {}

export function registerActionUtils(key: string, utils: IActionUtils<any>) {
  fileLog.log('[registerActionUtils] key:', key)
  if (key in ActionUtilsMap) {
    throw new Error(`ActionUtilsMap already has key: ${key}`)
  }
  ActionUtilsMap[key] = utils
  fileLog.log('[registerActionUtils] 注册成功, 当前所有keys:', Object.keys(ActionUtilsMap))
}

export function getActionUtils<T extends IAction>(key: string): IActionUtils<T> {
  fileLog.log('[getActionUtils] key:', key)
  const utils = ActionUtilsMap[key]
  if (!utils) {
    fileLog.log('[getActionUtils] 未找到, 可用的keys:', Object.keys(ActionUtilsMap))
  } else {
    fileLog.log('[getActionUtils] 找到')
  }
  return utils
}

export function createActionUtils<T extends IAction>(
  type: string,
  init: () => void,
  createDefaultData: () => T,
  UI: (data: T) => React.ReactNode
): IActionUtils<T> {
  fileLog.log('[createActionUtils] type:', type)
  const res: IActionUtils<T> = {
    type,
    mergeDataToDefaultData: createMergeToDefaultData(createDefaultData),
    createDefaultData,
    create: (actionGroup: IActionGroup, data?: Partial<T>) => {
      fileLog.log('[createActionUtils.create] actionGroup.ulid:', actionGroup.ulid)
      const action = createDefaultData()
      fileLog.log('[createActionUtils.create] action.ulid:', action.ulid)
      if (data) {
        fileLog.log('[createActionUtils.create] merging data')
        Object.assign(action, data)
      }
      // 修改：只维护children（web端主要使用），list由后端维护
      if (!actionGroup.children) {
        fileLog.log('[createActionUtils.create] children未初始化，初始化为[]')
        actionGroup.children = []
      }
      actionGroup.children.push(action)
      fileLog.log('[createActionUtils.create] pushed to children, length:', actionGroup.children.length)
    },
    update: (action: T, data?: Partial<T>) => {
      fileLog.log('[createActionUtils.update] action.ulid:', action.ulid)
      mergeDataWith(action, data)
    },
    UI,
    init,
  }
  registerActionUtils(type, res)
  fileLog.log('[createActionUtils] 注册完成 type:', type)
  return res
}
